import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner, Text, Switch } from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { ssoButtonStyle } from '@/features/accounts/components/ssoButtonStyle';
import {
  getContextNoviSso,
  updateContextNoviSso,
  type NoviSsoConfig,
} from '@/api/profile';
import { uploadFileToS3 } from '@/api';
import { useFileRef, useFileRefRefetch, useUnconfirmedSaves, type SaveRead } from '@/features/files';
import { formMessages } from '@/constants';
import { SERVER_FAILURE_MESSAGE } from '@/constants/alertMessages';
import { classifyFailure } from '@/api/failureClass';
import { reportClientError } from '@/api/clientErrors';
import { traceIdFromError } from '@/api/errorTrace';
import { ErrorReference } from '@/components/errors';
import { Actions, Icons, vars, borders } from '@/theme';
import { testIds } from '@/test/testIds';

const t = testIds.contextSettings;

// PNG/SVG only — the button icon must keep its alpha (served as the original
// asset, never Thumbor), same as the header logo. No crop server-side.
const ICON_ACCEPT = '.png,.svg,image/png,image/svg+xml';
const LABEL_MAX = 12;
// Stand-in brand word so the preview button never reads "Continue with " — the
// backend defaults an unset label to the network slug (uppercased) at runtime.
const PREVIEW_FALLBACK_LABEL = 'NOVI';
// The login SSO buttons render inside the sign-in card (Paper maxWidth 440,
// padding 32 → ~376px of content). Cap the preview to that so it's the SAME
// size users actually see, not the full (wider) settings-dialog width.
const LOGIN_BUTTON_MAX_WIDTH = 376;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const fieldStyle = {
  width: '100%',
  height: 36,
  padding: '0 10px',
  fontSize: 14,
  fontFamily: "'Lato', sans-serif",
  border: `1px solid ${vars.color.surface.border}`,
  borderRadius: borders.radius.sm,
  boxSizing: 'border-box' as const,
  backgroundColor: vars.color.fixed.white,
  color: vars.color.neutral.dark,
};

function Field({ children }: { children: React.ReactNode }) {
  return <div style={{ marginBottom: 10 }}>{children}</div>;
}

/**
 * #1338 (Q-374): the "Check the fields" line is said only when the server named
 * the fields (a 400 with a LIST detail). A string detail from a 4xx is shown as
 * is (e.g. "Enabling NOVI SSO requires ..."); a server failure, a failure with
 * no usable detail, or an open circuit says so; a transport failure says
 * unreachable.
 */
function saveFailureMessage(err: unknown): string {
  const cls = classifyFailure(err);
  if (cls === 'transport_timeout' || cls === 'transport_network') {
    return formMessages.connectivity.unreachable;
  }
  if (cls !== 'client') return SERVER_FAILURE_MESSAGE;
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) return formMessages.settingsForms.noviSsoCheckFields;
  return SERVER_FAILURE_MESSAGE;
}

/**
 * NoviSsoEditor — per-network NOVI SSO config, in the network-admin settings area
 * (same gate as the logos: a member with can_edit_logo, or staff).
 *
 * The whole section is collapsed behind the enable toggle on the header line:
 * the connection settings only appear once SSO is switched on. The toggle's
 * value IS the saved `enabled` flag — turning it off and saving disables SSO.
 *
 * WYSIWYG: the admin edits the ACTUAL sign-in button users will get, in place —
 * the button renders at the exact login size; clicking its icon swaps the image
 * (same raw-asset upload as the header logo, no crop), and the brand word is an
 * inline text field right where it appears on the button.
 *
 * Drives GET/PUT /api/context/novi-sso/. The OAuth client secret is write-only:
 * we never receive it, only whether one is stored — so the field shows a "saved"
 * placeholder and is sent only when the admin types a new value. The button icon
 * is left untouched unless changed (uploaded → new key, or removed → null).
 */
export function NoviSsoEditor() {
  const [config, setConfig] = useState<NoviSsoConfig | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState(''); // empty = leave unchanged
  const [label, setLabel] = useState('');
  // undefined = icon unchanged; null = removed; File = newly picked.
  const [iconChange, setIconChange] = useState<File | null | undefined>(undefined);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The caught error object (not just its message) so the failure carries a
  // trace id. This editor calls the API directly (not via react-query), so the
  // central router never sees these failures — we report them here instead.
  const [errorObj, setErrorObj] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  // The S3 key of an icon uploaded in this session. It keys the local echo
  // registered by uploadFileToS3, which is what keeps the icon on screen after
  // a save while the server is still processing it (§7.1 row 10: the preview
  // used to be cleared on save and the icon vanished).
  const [savedLogoKey, setSavedLogoKey] = useState<string | null>(null);

  const iconInputRef = useRef<HTMLInputElement>(null);

  // #939: the icon save record (one slot). A config re-read that hands back
  // the icon a save replaced is stale (a lagging replica): it keeps the poll
  // going and paints nothing (I2) until the new icon's outcome arrives.
  const iconSaves = useUnconfirmedSaves<'logo'>();
  // I7 needs to know which read the screen shows. This editor reads the
  // config without react-query, so it counts its reads the way a query
  // counts `dataUpdateCount` (the PUT response is a seed, not a read).
  const configReadsRef = useRef({ count: 0, inFlight: 0 });
  const [configRead, setConfigRead] = useState<SaveRead>({ updateCount: 0, updatedAt: 0 });
  const adoptRead = useCallback((c: NoviSsoConfig) => {
    configReadsRef.current.count += 1;
    setConfig(c);
    setConfigRead({ updateCount: configReadsRef.current.count, updatedAt: Date.now() });
  }, []);

  useEffect(() => {
    let active = true;
    getContextNoviSso()
      .then((c) => {
        if (!active) return;
        adoptRead(c);
        setEnabled(c.enabled);
        setBaseUrl(c.baseUrl ?? '');
        setClientId(c.clientId ?? '');
        setLabel(c.label ?? '');
      })
      .catch((err) => {
        if (!active) return;
        reportClientError({ error: err, source: 'query', traceId: traceIdFromError(err) });
        setErrorObj(err);
        setError('Could not load NOVI SSO settings.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [adoptRead]);

  // I2, masking at the owner: while the icon save is unconfirmed, a stale
  // read shows the write's seed, never the replaced (failed) icon.
  const logoStale = iconSaves.unconfirmed('logo', config?.logo, configRead);
  const shownLogo = logoStale ? iconSaves.seed('logo') : config?.logo;

  // Display precedence (§14.3.6): a staged pick wins (it's what Save will
  // send), then the server rendition, then this session's local echo — so the
  // icon never disappears between "saved" and "processed".
  const logoView = useFileRef(shownLogo, { localKey: savedLogoKey });
  const displayIcon =
    iconChange instanceof File ? iconPreview : iconChange === null ? null : logoView.url;
  // Only speak for the SAVED icon: a staged removal must read as "no icon",
  // not as the stored one's processing state.
  const iconUnchanged = iconChange === undefined;
  const iconProcessing = iconUnchanged && !displayIcon && logoView.state === 'processing';
  const iconFailed = iconUnchanged && !displayIcon && logoView.state === 'failed';

  // Re-read the config while the icon is processing so the rendition lands on
  // its own (§14.3.7). Only `config` is replaced — never the fields the admin
  // may be typing into.
  const reloadConfig = useCallback(async () => {
    configReadsRef.current.inFlight += 1;
    try {
      adoptRead(await getContextNoviSso());
    } catch {
      /* a failed background refresh must not disturb the form */
    } finally {
      configReadsRef.current.inFlight -= 1;
    }
  }, [adoptRead]);
  useFileRefRefetch([config?.logo], () => void reloadConfig(), { unconfirmed: logoStale });

  // Show Save only once something actually differs from what was loaded — a
  // typed secret or any staged icon change both count as edits. After a save we
  // reset the secret/icon and adopt the returned config, so this goes false again.
  const isDirty =
    !!config &&
    (enabled !== config.enabled ||
      baseUrl !== (config.baseUrl ?? '') ||
      clientId !== (config.clientId ?? '') ||
      label !== (config.label ?? '') ||
      clientSecret !== '' ||
      iconChange !== undefined);

  // Picking/removing the icon just STAGES it — the actual upload happens in
  // handleSave alongside the rest of the config (one PUT).
  const handleIconPick = async (file: File) => {
    setIconChange(file);
    try {
      setIconPreview(await readFileAsDataUrl(file));
    } catch {
      /* preview is best-effort */
    }
  };

  const handleIconRemove = () => {
    setIconChange(null);
    setIconPreview(null);
  };

  const onIconInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleIconPick(file);
    e.target.value = ''; // allow re-picking the same file
  };

  const handleSave = async () => {
    setError(null);
    setErrorObj(null);
    setSaved(false);
    setSaving(true);
    // The icon this save replaces: a re-read that hands it back is stale.
    const replacedLogo = config?.logo;
    try {
      let logoKey: string | null | undefined;
      if (iconChange instanceof File) {
        logoKey = await uploadFileToS3(iconChange);
        setSavedLogoKey(logoKey);
      } else if (iconChange === null) {
        setSavedLogoKey(null);
        logoKey = null; // explicit clear
      } // else undefined → omit, leave the icon untouched

      const updated = await updateContextNoviSso({
        enabled,
        baseUrl: baseUrl.trim() || null,
        clientId: clientId.trim() || null,
        clientSecret: clientSecret ? clientSecret : undefined,
        label: label.trim() || null,
        logoKey,
      });

      setConfig(updated);
      if (iconChange instanceof File) {
        iconSaves.markSaved('logo', replacedLogo, updated.logo, {
          updateCount: configReadsRef.current.count,
          fetching: configReadsRef.current.inFlight > 0,
        });
      } else if (iconChange === null) {
        iconSaves.clear('logo'); // removed: nothing left to confirm
      } // else the icon was untouched: no record
      setClientSecret(''); // never retain the typed secret
      setIconChange(undefined);
      setIconPreview(null);
      setSaved(true);
    } catch (err) {
      reportClientError({ error: err, source: 'mutation', traceId: traceIdFromError(err) });
      setErrorObj(err);
      setError(saveFailureMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={t.noviSsoSection}>
      {/* Header line: the section label and its enable toggle share a row; the
          settings below only render when the toggle is on. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <Text variant="smallLabel" color="secondary">
          Member SSO (NOVI)
        </Text>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          shape="square"
          disabled={loading || saving}
          aria-label="Enable NOVI single sign-on"
          data-testid={t.noviSsoEnabledToggle}
        />
      </div>

      {loading ? (
        <Text variant="caption" color="muted">
          Loading…
        </Text>
      ) : (
        enabled && (
          <>
            {/* The actual sign-in button users will see, editable in place:
                same size as login, click the icon to swap it, type the word. */}
            <div
              data-testid={t.noviSsoButtonPreview}
              style={{ ...ssoButtonStyle, cursor: 'default', maxWidth: LOGIN_BUTTON_MAX_WIDTH, margin: '0 auto 8px' }}
            >
              <input
                ref={iconInputRef}
                type="file"
                accept={ICON_ACCEPT}
                onChange={onIconInputChange}
                style={{ display: 'none' }}
                disabled={saving}
              />
              {/* Icon slot — click to replace; small × to revert to the glyph. */}
              <span
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => iconInputRef.current?.click()}
                  disabled={saving}
                  title="Change button icon"
                  aria-label="Change button icon"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: saving ? 'default' : 'pointer',
                    color: vars.color.neutral.dark,
                  }}
                >
                  {displayIcon ? (
                    // Same dimensions the icon gets on the real login button.
                    <img src={displayIcon} alt="" style={{ height: 18, width: 'auto', maxWidth: 18 }} />
                  ) : iconProcessing ? (
                    // Uploaded, not rendered yet (e.g. after a reload) — say so
                    // rather than showing the "no icon" affordance.
                    <span data-testid={t.noviSsoIconProcessing} title={formMessages.upload.processing('a few seconds')}>
                      <Spinner size={14} />
                    </span>
                  ) : (
                    // No icon yet — show an upload affordance, not the login
                    // fallback glyph: this slot is "click to add an icon".
                    <Icons.upload style={{ fontSize: 18, color: vars.color.neutral.subtle }} />
                  )}
                </button>
                {displayIcon && (
                  <button
                    type="button"
                    onClick={handleIconRemove}
                    disabled={saving}
                    title="Remove button icon"
                    aria-label="Remove button icon"
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -8,
                      width: 14,
                      height: 14,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      border: 'none',
                      borderRadius: '50%',
                      background: vars.color.neutral.base,
                      color: vars.color.fixed.white,
                      cursor: saving ? 'default' : 'pointer',
                    }}
                  >
                    <Icons.close style={{ fontSize: 10 }} />
                  </button>
                )}
              </span>

              {/* "Continue with {label}" must read as ONE label — group the
                  static text and the editable word together so the button's
                  flex gap only sits between the icon and the text (exactly like
                  login), with a normal single space before the word. */}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                Continue with{' '}
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value.slice(0, LABEL_MAX))}
                  maxLength={LABEL_MAX}
                  size={Math.max((label || PREVIEW_FALLBACK_LABEL).length, 1)}
                  placeholder={PREVIEW_FALLBACK_LABEL}
                  aria-label="Button word"
                  disabled={saving}
                  data-testid={t.noviSsoLabelInput}
                  style={{
                    // Inherit the button's exact type so the word renders
                    // identically to login; only a faint underline marks it
                    // editable.
                    font: 'inherit',
                    color: 'inherit',
                    background: 'none',
                    border: 'none',
                    borderBottom: `1px dashed ${vars.color.neutral.subtle}`,
                    padding: 0,
                    margin: 0,
                    textAlign: 'left',
                    outline: 'none',
                    boxSizing: 'content-box',
                  }}
                />
              </span>
            </div>

            {iconFailed && (
              <Text
                variant="caption"
                color="error"
                data-testid={t.noviSsoIconFailed}
                style={{ display: 'block', textAlign: 'center', marginBottom: 4 }}
              >
                {formMessages.upload.failed} — {formMessages.upload.retry}
              </Text>
            )}

            <Text variant="caption" color="muted" style={{ display: 'block', textAlign: 'center', marginBottom: 12 }}>
              Upload a PNG/SVG and enter text (up to {LABEL_MAX} chars)
            </Text>

            <Field>
              <input
                type="text"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="NOVI tenant URL (https://your-org.noviams.com)"
                aria-label="NOVI tenant URL"
                disabled={saving}
                data-testid={t.noviSsoBaseUrlInput}
                style={fieldStyle}
              />
            </Field>

            <Field>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="OAuth client ID"
                aria-label="OAuth client ID"
                disabled={saving}
                data-testid={t.noviSsoClientIdInput}
                style={fieldStyle}
              />
            </Field>

            <Field>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={config?.clientSecretSet ? '•••••••• (saved — leave blank to keep)' : 'OAuth client secret'}
                aria-label="OAuth client secret"
                autoComplete="new-password"
                disabled={saving}
                data-testid={t.noviSsoClientSecretInput}
                style={fieldStyle}
              />
            </Field>

            {isDirty && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <ActionButton
                  action={Actions.save}
                  mode="labeled"
                  size="small"
                  showIcon={false}
                  label="Save"
                  loading={saving}
                  loadingLabel="Saving…"
                  disabled={saving}
                  onClick={handleSave}
                  testIdOverride={t.noviSsoSaveButton}
                />
              </div>
            )}
          </>
        )
      )}

      {error && (
        <>
          <Text variant="caption" color="error" style={{ display: 'block', marginTop: 6 }}>
            {error}
          </Text>
          <ErrorReference error={errorObj} />
        </>
      )}
      {saved && !error && (
        <Text variant="caption" color="success" style={{ display: 'block', marginTop: 6 }}>
          Saved.
        </Text>
      )}
    </div>
  );
}
